import {
  Box,
  Checkbox,
  CircularProgress,
  IconButton,
  InputAdornment,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import {
  ArrowDownward as ArrowDownIcon,
  ArrowUpward as ArrowUpIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Clear as ClearIcon,
  FilterAlt as FilterIcon,
  FilterAltOff as FilterOffIcon,
  Search as SearchIcon,
  ViewColumn as ViewColumnIcon,
} from '@mui/icons-material';
import { isValidElement, useEffect, useMemo, useState } from 'react';
import type { ComponentType, ReactNode } from 'react';
import type { SvgIconProps } from '@mui/material';
import { EmptyState } from './EmptyState';

/**
 * Lightweight table primitive. Wraps the loading-spinner / empty-state /
 * error-banner triad so call sites don't reinvent it. Opt-in features
 * (each off by default to keep the simple case simple):
 *
 *   - searchable          → toolbar text input filters rows across columns
 *   - column.sortable     → clickable header toggles asc/desc
 *   - column.filter       → per-column text or select filter chip
 *   - column visibility   → toolbar menu hides/shows columns
 *   - paginated           → client-side pagination footer (page size + nav)
 *
 * Pages doing server-side paging (Spring Page<T>) keep using the external
 * {@link Pagination} component below. Pages with already-fetched arrays
 * just set `paginated`.
 */

type Primitive = string | number | boolean | null | undefined;

export interface ColumnFilter<T> {
  /** 'text' shows a TextField, 'select' shows a dropdown of distinct values. */
  type: 'text' | 'select';
  /** For select filters; if omitted the table derives them from `getFilterValue`. */
  options?: { label: string; value: string }[];
  /** Predicate; defaults to substring match against `getFilterValue(row)`. */
  match?: (row: T, query: string) => boolean;
  /** Extracts the comparable value from a row (defaults to coercing the cell). */
  getFilterValue?: (row: T) => Primitive;
}

export interface Column<T> {
  /** Header label. */
  header: ReactNode;
  /** Cell renderer, given the row. */
  cell: (row: T) => ReactNode;
  /** CSS width or px number. */
  width?: number | string;
  /** Right-align numeric columns by passing 'right'. */
  align?: 'left' | 'right' | 'center';
  /** Stable id for hide/show + filter state. Defaults to the header text or index. */
  key?: string;
  /** Click header → toggle asc/desc on this column. */
  sortable?: boolean;
  /** Comparable value for sorting. Defaults to a stringified `cell` output. */
  sortValue?: (row: T) => Primitive;
  /** Searchable representation of the row for the toolbar search box. Defaults to `cell` text. */
  searchValue?: (row: T) => string;
  /** Per-column filter config. Adds a chip in the column header. */
  filter?: ColumnFilter<T>;
  /** When true, the column can't be hidden via the visibility menu. */
  pinned?: boolean;
}

interface DataTableProps<T> {
  rows: T[] | undefined;
  columns: Column<T>[];
  rowKey: (row: T) => string;
  /** Loading spinner overlay. SWR's `isLoading`. */
  isLoading?: boolean;
  /** Error banner if set. */
  error?: { message: string } | null;
  /** Empty-state config. Required so empty lists are never just "blank". */
  empty: {
    icon?: ComponentType<SvgIconProps>;
    title: ReactNode;
    description?: ReactNode;
    action?: ReactNode;
  };
  /** Row click handler — gets the row, not the event. */
  onRowClick?: (row: T) => void;
  // ─── Optional toolbar features ──────────────────────────────────
  /** Show a search box in the toolbar that filters rows across columns. */
  searchable?: boolean;
  searchPlaceholder?: string;
  /** Show a column-visibility menu. Defaults true when columns have keys. */
  columnVisibility?: boolean;
  /** Client-side pagination. Set to true or pass a default page size. */
  paginated?: boolean;
  defaultPageSize?: number;
  /** Extra toolbar content rendered to the right of the search box. */
  toolbar?: ReactNode;
}

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100];

export function DataTable<T>({
  rows,
  columns,
  rowKey,
  isLoading,
  error,
  empty,
  onRowClick,
  searchable = false,
  searchPlaceholder = 'Search…',
  columnVisibility = true,
  paginated = false,
  defaultPageSize = 25,
  toolbar,
}: DataTableProps<T>) {
  const theme = useTheme();

  // Stable keys for columns (used by visibility + filters).
  const cols = useMemo<Column<T>[]>(
    () =>
      columns.map((c, idx) => ({
        ...c,
        key: c.key ?? (typeof c.header === 'string' ? c.header : `col-${idx}`),
      })),
    [columns],
  );

  const [search, setSearch] = useState('');
  const [sortKey, setSortKey] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('asc');
  const [colFilters, setColFilters] = useState<Record<string, string>>({});
  const [hidden, setHidden] = useState<Set<string>>(new Set());
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(defaultPageSize);

  // Reset page when filters change so we never strand the user past the end.
  useEffect(() => {
    setPage(0);
  }, [search, sortKey, sortDir, colFilters, pageSize, rows?.length]);

  const visibleCols = useMemo(() => cols.filter((c) => !hidden.has(c.key!)), [cols, hidden]);

  const filtered = useMemo(() => {
    if (!rows) return [];
    let list = rows;

    // Free-text search across columns.
    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter((row) =>
        cols.some((c) => {
          const v = c.searchValue ? c.searchValue(row) : extractText(c.cell(row));
          return v.toLowerCase().includes(q);
        }),
      );
    }

    // Per-column filters.
    Object.entries(colFilters).forEach(([key, value]) => {
      if (!value) return;
      const col = cols.find((c) => c.key === key);
      if (!col?.filter) return;
      const lc = value.toLowerCase();
      list = list.filter((row) => {
        if (col.filter!.match) return col.filter!.match(row, value);
        if (col.filter!.type === 'select') {
          const v = col.filter!.getFilterValue ? col.filter!.getFilterValue(row) : extractText(col.cell(row));
          return String(v ?? '').toLowerCase() === lc;
        }
        const v = col.filter!.getFilterValue ? col.filter!.getFilterValue(row) : extractText(col.cell(row));
        return String(v ?? '').toLowerCase().includes(lc);
      });
    });

    // Sort.
    if (sortKey) {
      const col = cols.find((c) => c.key === sortKey);
      if (col?.sortable) {
        const get = col.sortValue ?? ((row: T) => extractText(col.cell(row)));
        list = [...list].sort((a, b) => compareValues(get(a), get(b)) * (sortDir === 'asc' ? 1 : -1));
      }
    }

    return list;
  }, [rows, search, colFilters, sortKey, sortDir, cols]);

  const total = filtered.length;
  const pageStart = paginated ? page * pageSize : 0;
  const pageEnd = paginated ? Math.min(pageStart + pageSize, total) : total;
  const paged = paginated ? filtered.slice(pageStart, pageEnd) : filtered;

  // ─── error / loading / empty short-circuits ──────────────────────
  if (error) {
    return (
      <Box
        sx={{
          p: 3,
          bgcolor: theme.palette.status.dangerBg,
          color: theme.palette.status.danger,
          borderRadius: theme.radius.md + 'px',
          border: `1px solid ${theme.palette.status.danger}33`,
        }}
      >
        <Typography variant="body2" fontWeight={600}>
          Couldn't load this list.
        </Typography>
        <Typography variant="caption">{error.message}</Typography>
      </Box>
    );
  }

  if (isLoading && (!rows || rows.length === 0)) {
    return (
      <Box sx={{ py: 8, display: 'grid', placeItems: 'center' }}>
        <CircularProgress size={24} />
      </Box>
    );
  }

  const showToolbar = searchable || columnVisibility || !!toolbar;
  const noUnfiltered = !rows || rows.length === 0;

  return (
    <Box>
      {showToolbar && (
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1.5}
          alignItems={{ sm: 'center' }}
          sx={{ mb: 1.5 }}
        >
          {searchable && (
            <TextField
              size="small"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder={searchPlaceholder}
              sx={{ minWidth: 280 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ fontSize: 18, color: 'text.tertiary' }} />
                  </InputAdornment>
                ),
                endAdornment: search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setSearch('')} aria-label="clear search">
                      <ClearIcon sx={{ fontSize: 16 }} />
                    </IconButton>
                  </InputAdornment>
                ) : null,
              }}
            />
          )}
          {toolbar}
          <Box sx={{ flex: 1 }} />
          {Object.values(colFilters).some(Boolean) && (
            <Tooltip title="Clear all column filters">
              <IconButton size="small" onClick={() => setColFilters({})} aria-label="clear filters">
                <FilterOffIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {columnVisibility && (
            <ColumnVisibilityMenu
              columns={cols}
              hidden={hidden}
              onChange={setHidden}
            />
          )}
        </Stack>
      )}

      {noUnfiltered ? (
        <EmptyState {...empty} />
      ) : (
        <TableContainer
          sx={{
            bgcolor: theme.palette.surface.card,
            border: `1px solid ${theme.palette.border.subtle}`,
            borderRadius: theme.radius.md + 'px',
            // Horizontal scroll so wide column sets stay reachable.
            // Vertical kept hidden — the page itself scrolls.
            overflowX: 'auto',
            overflowY: 'hidden',
            // Always-visible thin scrollbar (default WebKit hides it on
            // Mac trackpads, which makes overflow feel broken).
            '&::-webkit-scrollbar': { height: 10 },
            '&::-webkit-scrollbar-track': {
              bgcolor: theme.palette.surface.sunken,
              borderBottomLeftRadius: theme.radius.md + 'px',
              borderBottomRightRadius: theme.radius.md + 'px',
            },
            '&::-webkit-scrollbar-thumb': {
              bgcolor: theme.palette.border.strong ?? theme.palette.border.subtle,
              borderRadius: 8,
              '&:hover': { bgcolor: theme.palette.text.tertiary },
            },
            scrollbarWidth: 'thin',
          }}
        >
          <Table size="small" sx={{ minWidth: 'max-content' }}>
            <TableHead>
              <TableRow>
                {visibleCols.map((col) => {
                  const isSorted = sortKey === col.key;
                  return (
                    <TableCell
                      key={col.key}
                      align={col.align ?? 'left'}
                      sx={{
                        width: col.width,
                        bgcolor: theme.palette.surface.sunken,
                        fontSize: 11,
                        letterSpacing: 0.4,
                        textTransform: 'uppercase',
                        color: theme.palette.text.tertiary,
                        borderBottom: `1px solid ${theme.palette.border.subtle}`,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      <Stack direction="row" spacing={0.5} alignItems="center" justifyContent={col.align === 'right' ? 'flex-end' : 'flex-start'}>
                        <Box
                          component={col.sortable ? 'button' : 'span'}
                          onClick={
                            col.sortable
                              ? () => {
                                  if (sortKey === col.key) setSortDir(sortDir === 'asc' ? 'desc' : 'asc');
                                  else { setSortKey(col.key!); setSortDir('asc'); }
                                }
                              : undefined
                          }
                          sx={{
                            background: 'transparent',
                            border: 0,
                            color: 'inherit',
                            font: 'inherit',
                            letterSpacing: 'inherit',
                            textTransform: 'inherit',
                            cursor: col.sortable ? 'pointer' : 'default',
                            p: 0,
                          }}
                        >
                          {col.header}
                        </Box>
                        {isSorted && (sortDir === 'asc'
                          ? <ArrowUpIcon sx={{ fontSize: 12 }} />
                          : <ArrowDownIcon sx={{ fontSize: 12 }} />)}
                        {col.filter && (
                          <ColumnFilterMenu
                            col={col}
                            rows={rows ?? []}
                            value={colFilters[col.key!] ?? ''}
                            onChange={(v) => setColFilters((prev) => ({ ...prev, [col.key!]: v }))}
                          />
                        )}
                      </Stack>
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {paged.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={visibleCols.length} sx={{ py: 6 }}>
                    <Stack alignItems="center" spacing={1}>
                      <Typography variant="body2" color="text.secondary">No rows match.</Typography>
                      <Typography variant="caption" color="text.tertiary">Loosen the search or column filters.</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : (
                paged.map((row) => (
                  <TableRow
                    key={rowKey(row)}
                    hover={!!onRowClick}
                    onClick={onRowClick ? () => onRowClick(row) : undefined}
                    sx={{
                      cursor: onRowClick ? 'pointer' : 'default',
                      transition: `background-color ${theme.motion.durations.fast}ms ${theme.motion.easings.standard}`,
                      '&:last-child td': { borderBottom: 0 },
                    }}
                  >
                    {visibleCols.map((col) => (
                      <TableCell key={col.key} align={col.align ?? 'left'} sx={{ fontSize: 13 }}>
                        {col.cell(row)}
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
          {isLoading && (
            <Stack direction="row" justifyContent="center" sx={{ py: 1.5, bgcolor: theme.palette.surface.sunken }}>
              <CircularProgress size={14} />
            </Stack>
          )}
        </TableContainer>
      )}

      {paginated && !noUnfiltered && (
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mt: 1.5, color: theme.palette.text.secondary }}
        >
          <Typography variant="caption">
            {total === 0
              ? 'No rows.'
              : `${(pageStart + 1).toLocaleString()}–${pageEnd.toLocaleString()} of ${total.toLocaleString()}`}
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <TextField
              size="small"
              select
              value={String(pageSize)}
              onChange={(e) => setPageSize(Number(e.target.value))}
              SelectProps={{ native: true }}
              sx={{ minWidth: 80 }}
              variant="standard"
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>{n} / page</option>
              ))}
            </TextField>
            <IconButton
              size="small"
              disabled={page === 0}
              onClick={() => setPage((p) => Math.max(0, p - 1))}
              aria-label="previous page"
            >
              <ChevronLeftIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" sx={{ px: 0.5 }}>
              Page {page + 1} / {Math.max(Math.ceil(total / pageSize), 1)}
            </Typography>
            <IconButton
              size="small"
              disabled={pageEnd >= total}
              onClick={() => setPage((p) => p + 1)}
              aria-label="next page"
            >
              <ChevronRightIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Stack>
      )}
    </Box>
  );
}

// ─── Column visibility menu ───────────────────────────────────────

function ColumnVisibilityMenu<T>({
  columns,
  hidden,
  onChange,
}: {
  columns: Column<T>[];
  hidden: Set<string>;
  onChange: (next: Set<string>) => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  return (
    <>
      <Tooltip title="Show / hide columns">
        <IconButton size="small" onClick={(e) => setAnchor(e.currentTarget)} aria-label="column visibility">
          <ViewColumnIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Menu open={!!anchor} anchorEl={anchor} onClose={() => setAnchor(null)} keepMounted>
        {columns.map((c) => (
          <MenuItem
            key={c.key}
            disabled={c.pinned}
            onClick={() => {
              const next = new Set(hidden);
              if (next.has(c.key!)) next.delete(c.key!);
              else next.add(c.key!);
              onChange(next);
            }}
          >
            <ListItemIcon>
              <Checkbox size="small" edge="start" checked={!hidden.has(c.key!)} disableRipple />
            </ListItemIcon>
            <ListItemText
              primary={typeof c.header === 'string' ? c.header : c.key}
              primaryTypographyProps={{ fontSize: 13 }}
            />
          </MenuItem>
        ))}
      </Menu>
    </>
  );
}

// ─── Per-column filter chip ───────────────────────────────────────

function ColumnFilterMenu<T>({
  col,
  rows,
  value,
  onChange,
}: {
  col: Column<T>;
  rows: T[];
  value: string;
  onChange: (next: string) => void;
}) {
  const [anchor, setAnchor] = useState<null | HTMLElement>(null);
  const filter = col.filter!;

  // Derive distinct values for select-type filters that didn't pass options.
  const options = useMemo(() => {
    if (filter.options) return filter.options;
    if (filter.type !== 'select') return [];
    const get = filter.getFilterValue ?? ((r: T) => extractText(col.cell(r)));
    const set = new Set<string>();
    rows.forEach((r) => {
      const v = String(get(r) ?? '').trim();
      if (v) set.add(v);
    });
    return [...set].sort().map((v) => ({ label: v, value: v }));
  }, [filter, rows, col]);

  return (
    <>
      <Tooltip title="Filter column">
        <IconButton
          size="small"
          onClick={(e) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          aria-label="filter column"
        >
          <FilterIcon
            sx={{
              fontSize: 14,
              color: value ? 'primary.main' : 'text.tertiary',
            }}
          />
        </IconButton>
      </Tooltip>
      <Menu
        open={!!anchor}
        anchorEl={anchor}
        onClose={() => setAnchor(null)}
        slotProps={{ paper: { sx: { p: 1.5, minWidth: 220 } } }}
      >
        {filter.type === 'text' ? (
          <TextField
            size="small"
            autoFocus
            placeholder="contains…"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            InputProps={{
              endAdornment: value ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => onChange('')} aria-label="clear filter">
                    <ClearIcon sx={{ fontSize: 16 }} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
          />
        ) : (
          <TextField
            size="small"
            select
            value={value}
            onChange={(e) => onChange(e.target.value)}
            fullWidth
            SelectProps={{ native: false }}
          >
            <MenuItem value=""><em>Any</em></MenuItem>
            {options.map((o) => (
              <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
            ))}
          </TextField>
        )}
      </Menu>
    </>
  );
}

// ─── helpers ──────────────────────────────────────────────────────

function extractText(node: ReactNode): string {
  if (node == null || typeof node === 'boolean') return '';
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (isValidElement(node)) {
    const props = (node as { props?: { children?: ReactNode } }).props;
    return extractText(props?.children ?? '');
  }
  return '';
}

function compareValues(a: Primitive, b: Primitive): number {
  if (a == null && b == null) return 0;
  if (a == null) return -1;
  if (b == null) return 1;
  if (typeof a === 'number' && typeof b === 'number') return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: 'base' });
}
